import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { Flipbook } from "@/components/site/flipbook/Flipbook";
import { ProjectGallery, ProjectVideo } from "@/components/site/ProjectMedia";
import { Reveal, EASE } from "@/components/site/SectionHead";
import { api, asset, CATEGORY_LABEL } from "@/lib/api";
import { useProjects } from "@/hooks/useProjects";

const Meta = ({ label, value }) => (
  <div className="border-t border-[#F5A524]/25 pt-3">
    <p className="font-mono text-[11px] tracking-[0.25em] text-zinc-400">{label}</p>
    <p className="mt-1 font-display font-bold text-2xl uppercase">{value || "—"}</p>
  </div>
);

export default function ProjectPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(false);
  const { projects } = useProjects();

  useEffect(() => {
    setProject(null);
    setError(false);
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => setError(true));
  }, [id]);

  const idx = projects.findIndex((p) => p.id === id);
  const next = projects.length > 1 ? projects[(idx + 1) % projects.length] : null;
  const extra = Array.isArray(project?.images) ? project.images : [];
  const gallery = project ? (project.pdf_url || project.video_url ? extra : [project.cover_url, ...extra].filter(Boolean)) : [];

  return (
    <main data-testid="project-page" className="bg-[#0B0C10] text-zinc-100 min-h-screen">
      <Nav />
      <div className="px-5 sm:px-10 lg:px-16 pt-28">
        <Link to="/" data-testid="project-back-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-[#F5A524] hover:gap-4 transition-all">
          <ArrowLeft size={14} /> BACK TO ARCHIVE
        </Link>
      </div>
      {error && <p data-testid="project-error" className="px-5 sm:px-10 lg:px-16 py-20 font-mono text-zinc-400">FILE NOT FOUND // 404</p>}
      {!project && !error && <p className="px-5 sm:px-10 lg:px-16 py-20 font-mono text-zinc-400">DECRYPTING<span className="blink">_</span></p>}
      {project && (
        <>
          <section className="px-5 sm:px-10 lg:px-16 pt-10 pb-16 grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <p className="font-mono text-xs tracking-[0.35em] text-[#F5A524] mb-5">// {CATEGORY_LABEL[project.category]} FILE</p>
              <h1 data-testid="project-title" className="font-display font-black uppercase leading-[0.85] tracking-tight text-[clamp(3rem,10vw,9rem)]">
                <span className="block overflow-hidden">
                  <motion.span className="block" initial={{ y: "105%" }} animate={{ y: 0 }} transition={{ duration: 1, ease: EASE }}>{project.title}</motion.span>
                </span>
              </h1>
              <Reveal delay={0.2}>
                <p data-testid="project-description" className="mt-8 max-w-2xl text-base sm:text-lg text-zinc-300 leading-relaxed whitespace-pre-line">{project.description}</p>
                {project.tags?.length > 0 && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {project.tags.map((t) => <span key={t} className="font-mono text-[11px] tracking-[0.2em] uppercase border border-[#F5A524]/30 px-3 py-1.5 text-zinc-300">{t}</span>)}
                  </div>
                )}
              </Reveal>
            </div>
            <Reveal delay={0.3} className="lg:col-span-5 grid grid-cols-3 gap-5">
              <Meta label="YEAR" value={project.year} />
              <Meta label="TYPE" value={CATEGORY_LABEL[project.category]} />
              <Meta label="FORMAT" value={project.video_url ? "VIDEO" : project.pdf_url ? "PDF" : project.images?.length ? `${project.images.length + 1} IMAGES` : "IMAGE"} />
            </Reveal>
          </section>

          <section className="px-5 sm:px-10 lg:px-16 pb-20 space-y-10">
            {project.video_url && <Reveal><ProjectVideo url={project.video_url} title={project.title} /></Reveal>}
            {project.pdf_url && <Reveal><Flipbook url={project.pdf_url} title={project.title} /></Reveal>}
            {gallery.length > 0 && <ProjectGallery images={gallery} title={project.title} />}
          </section>

          {next && next.id !== project.id && (
            <Link to={`/project/${next.id}`} data-testid="project-next-link"
              className="group block border-t border-[#F5A524]/20 px-5 sm:px-10 lg:px-16 py-16 hover:bg-[#F5A524] transition-colors duration-500">
              <p className="font-mono text-xs tracking-[0.3em] text-[#F5A524] group-hover:text-black">NEXT FILE</p>
              <p className="mt-3 font-display font-black uppercase text-5xl sm:text-8xl leading-none flex items-center gap-6 group-hover:text-black">
                {next.title} <ArrowRight className="w-10 h-10 sm:w-16 sm:h-16 transition-transform group-hover:translate-x-4" />
              </p>
            </Link>
          )}
        </>
      )}
      <Footer />
    </main>
  );
}
